import { Injectable } from '@angular/core';
import { environment } from '@tech-environments/environment';
import { AppConfig } from '@tech-utils/app-config.service';
import { ClientData, ClientDataLoginModel } from '@techrules/portallib';
import { QuestionnaireProfileEnum } from '../../areas/main/questionnaire/profile/questionnaire.enum';

export enum LocalStorageKey {
  LANGUAGE = 'Language',
  DOMAIN = 'Domain',
  SESSION_TOKEN = 'SessionClientToken',
  CLIENT_ID = 'ClientId',
  CLIENTCODE = 'ClientCode',
  PORTFOLIOCODE = 'PortfolioCode',
  FORMATS = 'FormatsTech',
  CONDITIONAL_FORMATS = 'ConditionalFormats',
  GROUP_SEPARATOR = 'GroupSeparator',
  DATE_FORMAT = 'DateFormat',
  DECIMAL_SEPARATOR = 'DecimalSeparator',
  NEGATIVE_SYMBOL = 'NegativeSymbol',
  NUMBER_OF_DECIMAL = 'NumberOfDecimal',
  IS_PRODUCTIVE = 'IsProductive',
  OPERATIVE_CODE = 'OperativeCode',
  OPERATIVE_INIT = 'OperativeInit',
  ACTIVITY_INIT = 'ActivityInit',
  PROFILE = 'Profile',
  ONBOARDING_PORTFOLIO = 'OnboardingPortfolio',
  SERVICE_MODEL_CODE = 'ServiceModelCode',
  USE_MODEL_PORTFOLIO_THEMES = 'useModelPortfolioThemes',
  VIEW_QUESTIONNAIRE_THEMES = 'viewQuestionnaireThemes',
}

@Injectable()
export class SessionService {
  private _language: string = undefined;
  private _domain: string = undefined;
  private _sessionToken: string = undefined;
  private _clientId: string = undefined;
  private _clientCode: string = undefined;
  private _clientDataLogin: ClientDataLoginModel;
  private _clientData: ClientData;
  private _portfolioCode: string = undefined;
  private _formats: [] = undefined;
  private _conditionalFormats: object = undefined;
  private _group_separator: string = undefined;
  private _date_format: string = undefined;
  private _decimal_separator: string = undefined;
  private _negative_symbol: string = undefined;
  private _number_of_decimal: any = undefined;
  private _isProductive: boolean = false;
  private _operativeCode: string = undefined;
  private _operativeInit: string = undefined;
  private _activityInit: boolean = undefined;
  private _profile: string = undefined;
  private _onboardingPortfolio: string = undefined;
  private _serviceModelCode: string = undefined;
  private _useModelPortfolioThemes: boolean = undefined;
  private _viewQuestionnaireThemes: boolean = undefined;

  constructor() {}

  removeItem(item: string): void {
    localStorage.removeItem(item);
  }
  /**
   * Getter $useModelPortfolioThemes
   * @return {useModelPortfolioThemes}
   */
  get viewQuestionnaireThemes(): boolean | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.VIEW_QUESTIONNAIRE_THEMES)
    ) {
      this._viewQuestionnaireThemes = JSON.parse(
        window.localStorage.getItem(LocalStorageKey.VIEW_QUESTIONNAIRE_THEMES)
      );
    } else {
      this._viewQuestionnaireThemes = false;
    }
    return this._viewQuestionnaireThemes;
  }

  /**
   * Setter $useModelPortfolioThemes
   * @parameter {useModelPortfolioThemes}
   */
  set viewQuestionnaireThemes(value: boolean) {
    window.localStorage.setItem(
      LocalStorageKey.VIEW_QUESTIONNAIRE_THEMES,
      value.toString()
    );
    this._viewQuestionnaireThemes = value;
  }

  /**
   * Getter $useModelPortfolioThemes
   * @return {useModelPortfolioThemes}
   */
  get useModelPortfolioThemes(): boolean | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.USE_MODEL_PORTFOLIO_THEMES)
    ) {
      this._useModelPortfolioThemes = JSON.parse(
        window.localStorage.getItem(LocalStorageKey.USE_MODEL_PORTFOLIO_THEMES)
      );
    } else {
      this._useModelPortfolioThemes =
        AppConfig.settings.useModelPortfolioThemes;
    }
    return this._useModelPortfolioThemes;
  }

  /**
   * Setter $useModelPortfolioThemes
   * @parameter {useModelPortfolioThemes}
   */
  set useModelPortfolioThemes(value: boolean) {
    this._useModelPortfolioThemes = value;
  }

  /**
   * Getter $serviceModelCode
   * @return {string}
   */
  get serviceModelCode(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.SERVICE_MODEL_CODE)
    ) {
      this._serviceModelCode = window.localStorage.getItem(
        LocalStorageKey.SERVICE_MODEL_CODE
      );
    }
    return this._serviceModelCode;
  }

  /**
   * Setter $serviceModelCode
   * @parameter {string}
   */

  set serviceModelCode(value: string) {
    window.localStorage.setItem(
      LocalStorageKey.SERVICE_MODEL_CODE,
      value.toString()
    );
    this._serviceModelCode = value;
  }

  /**
   * Getter $onboardingPortfolio
   * @return {string}
   */
  get onboardingPortfolio(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.ONBOARDING_PORTFOLIO)
    ) {
      this._onboardingPortfolio = window.localStorage.getItem(
        LocalStorageKey.ONBOARDING_PORTFOLIO
      );
    }
    return this._onboardingPortfolio;
  }

  /**
   * Setter $onboardingPortfolio
   * @parameter {string}
   */

  set onboardingPortfolio(value: string) {
    window.localStorage.setItem(
      LocalStorageKey.ONBOARDING_PORTFOLIO,
      value.toString()
    );
    this._onboardingPortfolio = value;
  }

  get profile(): string | undefined {
    if (!this._profile) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.PROFILE)
      ) {
        this._profile = window.localStorage.getItem(LocalStorageKey.PROFILE);
      } else {
        this._profile = QuestionnaireProfileEnum.PORTFOLIO;
      }
    }
    return this._profile;
  }

  /**
   * Setter $onboardingPortfolio
   * @parameter {string}
   */

  set profile(value: string) {
    window.localStorage.setItem(LocalStorageKey.PROFILE, value.toString());
    this._profile = value;
  }

  /**
   * Getter $operativeCode
   * @return {string}
   */
  get activityInit(): boolean | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.ACTIVITY_INIT)
    ) {
      this._activityInit = JSON.parse(
        window.localStorage.getItem(LocalStorageKey.ACTIVITY_INIT)
      );
    }
    return this._activityInit;
  }

  /**
   * Setter $operativeCode
   * @parameter {string}
   */

  set activityInit(value: boolean) {
    window.localStorage.setItem(
      LocalStorageKey.ACTIVITY_INIT,
      value.toString()
    );
    this._activityInit = value;
  }

  /**
   * Getter $operativeCode
   * @return {string}
   */
  get operativeInit(): string | undefined {
    if (!this._operativeInit) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.OPERATIVE_INIT)
      ) {
        this._operativeInit = window.localStorage.getItem(
          LocalStorageKey.OPERATIVE_INIT
        );
      } /*else {
        this._operativeInit = '';
      }*/
    }
    return this._operativeInit;
  }

  /**
   * Setter $operativeCode
   * @parameter {string}
   */

  set operativeInit(value: string) {
    window.localStorage.setItem(LocalStorageKey.OPERATIVE_INIT, value);
    this._operativeInit = value;
  }

  /**
   * Getter $operativeCode
   * @return {string}
   */
  get operativeCode(): string | undefined {
    if (!this._operativeCode) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.OPERATIVE_CODE)
      ) {
        this._operativeCode = window.localStorage.getItem(
          LocalStorageKey.OPERATIVE_CODE
        );
      } else {
        this._operativeCode = 'C';
      }
    }
    return this._operativeCode;
  }

  /**
   * Setter $operativeCode
   * @parameter {string}
   */
  set operativeCode(value: string) {
    window.localStorage.setItem(LocalStorageKey.OPERATIVE_CODE, value);
    this._operativeCode = value;
  }

  /**
   * Getter $language
   * @return {string}
   */
  get language(): string | undefined {
    if (!this._language) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.LANGUAGE)
      ) {
        this._language = window.localStorage.getItem(LocalStorageKey.LANGUAGE);
      } else {
        this._language = AppConfig.settings.language;
      }
    }
    return this._language;
  }

  /**
   * Setter $language
   * @parameter {string}
   */

  set language(value: string) {
    this._language = value;
  }

  /**
   * Getter $domain
   * @return {string}
   */
  get domain(): string | undefined {
    if (!this._domain) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.DOMAIN)
      ) {
        this._domain = window.localStorage.getItem(LocalStorageKey.DOMAIN);
      } else {
        this._domain = AppConfig.settings.domain;
      }
    }
    return this._domain;
  }

  /**
   * Setter $domain
   * @parameter {string}
   */

  set domain(value: string) {
    this._domain = value;
  }

  /**
   * Getter $sessionToken
   * @return {string}
   */
  get sessionToken(): string | undefined {
    if (!this._sessionToken) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.SESSION_TOKEN)
      ) {
        this._sessionToken = window.localStorage.getItem(
          LocalStorageKey.SESSION_TOKEN
        );
      } else {
        this._sessionToken = environment.defaultValues.sessionToken;
      }
    }
    return this._sessionToken;
  }

  /**
   * Setter $sessionToken
   * @parameter {string}
   */

  set sessionToken(value: string) {
    this._sessionToken = value;
  }

  /**
   * Getter $clientId
   * @return {string}
   */
  get clientId(): string | undefined {
    if (!this._clientId) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.CLIENT_ID)
      ) {
        this._clientId = window.localStorage.getItem(LocalStorageKey.CLIENT_ID);
      } else {
        this._clientId = environment.defaultValues.clientId;
      }
    }
    return this._clientId;
  }

  /**
   * Setter $clientId
   * @parameter {string}
   */
  set clientId(value: string) {
    this._clientId = value;
  }

  /**
   * Getter $userCode
   * @return {string}
   */
  get clientCode(): string | undefined {
    if (!this._clientCode) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.CLIENTCODE)
      ) {
        this._clientCode = window.localStorage.getItem(
          LocalStorageKey.CLIENTCODE
        );
      } else {
        this._clientCode = environment.defaultValues.clientCode;
      }
    }
    return this._clientCode;
  }

  /**
   * Setter $clientId
   * @parameter {string}
   */

  set clientCode(value: string) {
    this._clientCode = value;
  }

  /**
   * Setter $clientData
   * @parameter {ClientData}
   */

  set clientData(value: ClientData) {
    this._clientData = value;
  }

  /**
   * Getter $clientData
   * @return {ClientData}
   */
  get clientData(): ClientData | undefined {
    return this._clientData;
  }

  /**
   * Setter $clientDataLogin
   * @parameter {ClientDataLogin}
   */

  set clientDataLogin(value: ClientDataLoginModel) {
    this._clientDataLogin = value;
  }

  /**
   * Getter $clientDataLogin
   * @return {ClientDataLoginModel}
   */
  get clientDataLogin(): ClientDataLoginModel | undefined {
    return this._clientDataLogin;
  }

  /**
   * Getter $portfolioCode
   * @return {PortfolioCode}
   */
  get portfolioCode(): string | undefined {
    if (!this._portfolioCode) {
      if (
        window.localStorage &&
        window.localStorage.getItem(LocalStorageKey.PORTFOLIOCODE)
      ) {
        this._portfolioCode = window.localStorage.getItem(
          LocalStorageKey.PORTFOLIOCODE
        );
      } else {
        this._portfolioCode = environment.defaultValues.portfolioCode;
      }
    }
    return this._portfolioCode;
  }

  /**
   * Setter $portfolioCode
   * @parameter {portfolioCode}
   */

  set portfolioCode(value: string) {
    window.localStorage.setItem(LocalStorageKey.PORTFOLIOCODE, value);
    this._portfolioCode = value;
  }

  get formats(): [] {
    let cc: string;
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.FORMATS)
    ) {
      cc = window.localStorage.getItem(LocalStorageKey.FORMATS);
    } else {
      cc = environment.defaultValues.formatsTech;
    }
    this._formats = JSON.parse(cc);

    return this._formats;
  }

  get formatsStr(): string {
    let cc: string;
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.FORMATS)
    ) {
      cc = window.localStorage.getItem(LocalStorageKey.FORMATS);
    } else {
      cc = environment.defaultValues.formatsTech;
    }

    return cc;
  }

  get conditionalFormats(): object {
    let cc: string;
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.CONDITIONAL_FORMATS)
    ) {
      cc = window.localStorage.getItem(LocalStorageKey.CONDITIONAL_FORMATS);
    } else {
      cc = environment.defaultValues.conditionalFormats;
    }
    this._conditionalFormats = JSON.parse(cc);

    return this._conditionalFormats[0];
  }

  get groupSeparator(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.GROUP_SEPARATOR)
    ) {
      this._group_separator = window.localStorage.getItem(
        LocalStorageKey.GROUP_SEPARATOR
      );
    } else {
      this._group_separator = environment.defaultValues.GroupSeparator;
    }
    return this._group_separator;
  }

  get dateFormat(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.DATE_FORMAT)
    ) {
      this._date_format = window.localStorage.getItem(
        LocalStorageKey.DATE_FORMAT
      );
    } else {
      this._date_format = environment.defaultValues.DateFormat;
    }
    return this._date_format;
  }

  get decimalSeparator(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.DECIMAL_SEPARATOR)
    ) {
      this._decimal_separator = window.localStorage.getItem(
        LocalStorageKey.DECIMAL_SEPARATOR
      );
    } else {
      this._decimal_separator = environment.defaultValues.DecimalSeparator;
    }
    return this._decimal_separator;
  }

  get negativeSymbol(): string | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.NEGATIVE_SYMBOL)
    ) {
      this._negative_symbol = window.localStorage.getItem(
        LocalStorageKey.NEGATIVE_SYMBOL
      );
    } else {
      this._negative_symbol = environment.defaultValues.NEGATIVE_SYMBOL;
    }
    return this._negative_symbol;
  }

  get numberOfDecimal(): number | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.NUMBER_OF_DECIMAL)
    ) {
      this._number_of_decimal = window.localStorage.getItem(
        LocalStorageKey.NUMBER_OF_DECIMAL
      );
    } else {
      this._number_of_decimal = environment.defaultValues.NUMBER_OF_DECIMAL;
    }
    return this._number_of_decimal;
  }

  /**
   * Getter $isProductive
   * @return {isProductive}
   */
  get isProductive(): boolean | undefined {
    if (
      window.localStorage &&
      window.localStorage.getItem(LocalStorageKey.IS_PRODUCTIVE)
    ) {
      this._isProductive = JSON.parse(
        window.localStorage.getItem(LocalStorageKey.IS_PRODUCTIVE)
      );
    } else {
      this._isProductive = environment.defaultValues.isProductive;
    }
    return this._isProductive;
  }

  /**
   * Setter $isProductive
   * @parameter {isProductive}
   */
  set isProductive(value: boolean) {
    this._isProductive = value;
  }
}
